import { aggregate } from '@directus/sdk';
import { useQuery } from '@tanstack/react-query';

import { directusClient } from '../../services/directusClient/directusClient.ts';
import { usePromoterItemQuery } from '../promoter/usePromoterItemQuery.ts';
import type { Timeframe } from './usePaginatedEventsQuery.ts';

type EventStatus = 'created' | 'published' | 'declined';

export const useFilteredEventsCountQuery = ({
  status,
  categoryId,
  eventType,
  timeframe,
  enabled,
}: {
  status: EventStatus;
  categoryId?: number;
  eventType?: 'online' | 'live' | 'secret';
  timeframe?: Timeframe;
  enabled?: boolean;
}) => {
  const { data } = usePromoterItemQuery();

  return useQuery({
    queryKey: ['events', 'filtered_count', { status, categoryId, eventType, timeframe, promoterId: data?.id }],
    queryFn: async () => {
      const filter: Record<string, unknown> = {
        promoter: { _eq: data?.id },
        status: { _eq: status },
      };

      if (categoryId) {
        filter.category = { _eq: categoryId };
      }

      if (eventType) {
        filter.type = { _eq: eventType };
      }

      // Must match usePaginatedEventsQuery, or the pagination total disagrees with the list.
      if (timeframe === 'upcoming') {
        filter.end = { _gte: new Date().toISOString() };
      }

      const result = await directusClient.request(
        aggregate('events', {
          aggregate: { count: '*' },
          query: { filter },
        }),
      );

      return Number(result[0]?.count ?? 0);
    },
    enabled: enabled !== false && !!data?.id,
  });
};
